import type { ReactNode } from 'react';
import type { RequestFailure } from 'src/utils/requestError';
import type { AssistantSurface, ApiAssistantReply } from 'src/types/assistant';
import type { ConversationEntry, StoredConversations } from './conversationStore';

import React, {
  useRef,
  useMemo,
  useState,
  useEffect,
  useContext,
  useCallback,
  createContext,
} from 'react';

import { useSession } from 'src/hooks/useSession';
import { useHydrated } from 'src/hooks/useHydrated';

import { useAssistantMessage } from 'src/queries';

import { type AssistantFailure, describeAssistantFailure } from './assistantFailure';
import { loadConversations, saveConversations, forgetConversations } from './conversationStore';

/** One catalog's conversation as the window shows it. */
interface Conversation {
  entries: ConversationEntry[];
  conversationId?: string;
  failure: AssistantFailure | null;
  /** Messages the conversation takes today, from the last reply — a caption only: the api counts. */
  remaining: number | null;
  pending: boolean;
  text: string;
}

const EMPTY: Conversation = {
  entries: [],
  failure: null,
  remaining: null,
  pending: false,
  text: '',
};

/** Both catalogs' conversations, and whose they are (`null` until the owner is known). */
type Conversations = Record<AssistantSurface, Conversation> & { owner: string | null };

const NONE = { buy: EMPTY, rent: EMPTY };

export interface AssistantConversation extends Conversation {
  /** The conversation took every message it may, by the api's word. */
  over: boolean;
  setText: (text: string) => void;
  submit: () => Promise<void>;
  startOver: () => void;
}

const Context = createContext<{
  conversations: Conversations;
  setText: (surface: AssistantSurface, text: string) => void;
  submit: (surface: AssistantSurface) => Promise<void>;
  startOver: (surface: AssistantSurface) => void;
} | null>(null);

/**
 * Who the conversations belong to: the account signed in, or the visitor who has not signed in —
 * `null` while that is not known yet (the server; the browser while the session is checked).
 */
const useOwner = (): string | null => {
  const hydrated = useHydrated();
  const { account, isLoggedIn, isLoading, isChecked } = useSession();

  // Until the api has said, nobody is known: a dealer signed in through the cabinet is a visitor to
  // the storefront only until `/user/auth/me` answers, and must not lose their copy to the visitor.
  if (!hydrated || isLoading || !isChecked) return null;
  if (!isLoggedIn) return 'visitor';

  return account?.id ? `account:${account.id}` : null;
};

/**
 * The assistant's conversations, one per catalog, held above the routes (app#397): a conversation
 * no longer ends when the catalog leaves the screen — a car's page and Back keep it, and so does a
 * reload, through the tab's storage (`conversationStore`). A reply that arrives after the window was
 * closed, or the catalog left, lands in its conversation all the same.
 *
 * A conversation is its owner's (api#441). When the storefront changes hands — signed in, signed
 * out, another account — both conversations are wiped, what was typed with them, the tab's copy,
 * and any reply still on its way: the next person at a shared device never sees them.
 */
export const AssistantConversationsProvider = ({ children }: { children: ReactNode }) => {
  const owner = useOwner();
  const send = useAssistantMessage();
  const sendRef = useRef(send.mutateAsync);
  const [conversations, setConversations] = useState<Conversations>({ ...NONE, owner: null });
  const conversationsRef = useRef(conversations);
  const ownerNow = useRef(owner);
  // The owner the conversations in memory belong to, once one was known.
  const ownerRef = useRef<string | null>(null);
  // One more at every wipe of a catalog's conversation: a reply asked for before it is dropped when
  // it comes. One count per catalog — starting over on one never drops the other's reply.
  const generation = useRef<Record<AssistantSurface, number>>({ buy: 0, rent: 0 });
  const nextId = useRef(1);

  useEffect(() => {
    sendRef.current = send.mutateAsync;
    conversationsRef.current = conversations;
    ownerNow.current = owner;
  });

  useEffect(() => {
    if (!owner) return;

    const before = ownerRef.current;
    ownerRef.current = owner;

    if (before === null) {
      // The first owner this page knows: the tab's copy comes back, if it is theirs. A conversation
      // started before the session was confirmed (where visitors may ask) stays as it is.
      const stored = loadConversations(owner);

      setConversations((current) => {
        const next = { ...current, owner };

        for (const surface of ['buy', 'rent'] as const) {
          const kept = stored[surface];

          if (!kept || current[surface].entries.length > 0 || current[surface].pending) continue;

          const entries = kept.entries.map((entry) => ({ ...entry, id: nextId.current++ }));

          next[surface] = {
            ...EMPTY,
            entries,
            conversationId: kept.conversationId,
            text: kept.draft ?? current[surface].text,
          };
        }

        return next;
      });
      return;
    }

    if (before !== owner) {
      generation.current = { buy: generation.current.buy + 1, rent: generation.current.rent + 1 };
      forgetConversations();
      setConversations({ ...NONE, owner });
    }
  }, [owner]);

  // The tab's copy follows the conversations, under the owner they belong to — never the one before
  // under the one after, in the render where the hands changed and the wipe is still on its way. It
  // is written when the lines change, not at every key the person types.
  const belongsTo = conversations.owner;
  const { entries: buyEntries, conversationId: buyId } = conversations.buy;
  const { entries: rentEntries, conversationId: rentId } = conversations.rent;

  useEffect(() => {
    if (!owner || belongsTo !== owner) return;

    const kept: StoredConversations = {};

    if (buyEntries.length > 0) kept.buy = { entries: buyEntries, conversationId: buyId };
    if (rentEntries.length > 0) kept.rent = { entries: rentEntries, conversationId: rentId };

    saveConversations(owner, kept);
  }, [owner, belongsTo, buyEntries, buyId, rentEntries, rentId]);

  const update = useCallback(
    (surface: AssistantSurface, change: (current: Conversation) => Conversation) => {
      setConversations((current) => ({ ...current, [surface]: change(current[surface]) }));
    },
    []
  );

  // Nothing is written into a conversation that is not on screen (the hands are changing).
  const setText = useCallback(
    (surface: AssistantSurface, text: string) => {
      if (conversationsRef.current.owner !== ownerNow.current) return;
      update(surface, (current) => ({ ...current, text }));
    },
    [update]
  );

  const startOver = useCallback(
    (surface: AssistantSurface) => {
      generation.current = { ...generation.current, [surface]: generation.current[surface] + 1 };
      update(surface, () => EMPTY);
    },
    [update]
  );

  const submit = useCallback(
    async (surface: AssistantSurface) => {
      if (conversationsRef.current.owner !== ownerNow.current) return;

      const current = conversationsRef.current[surface];
      const message = current.text.trim();

      if (!message || current.pending || current.failure?.over || current.remaining === 0) return;

      const asked = generation.current[surface];
      const personEntry: ConversationEntry = {
        id: nextId.current++,
        role: 'person',
        text: message,
        cars: [],
        action: { type: 'none' },
      };
      const sending = {
        ...current,
        entries: [...current.entries, personEntry],
        failure: null,
        pending: true,
        text: '',
      };

      // Read again before the reply: a second submit in the same moment sees this one pending.
      conversationsRef.current = { ...conversationsRef.current, [surface]: sending };
      update(surface, () => sending);

      try {
        const reply: ApiAssistantReply = await sendRef.current({
          text: message,
          conversationId: current.conversationId,
          surface,
        });

        if (generation.current[surface] !== asked) return;

        update(surface, (latest) => ({
          ...latest,
          pending: false,
          conversationId: reply.conversationId,
          remaining: reply.remainingMessages,
          entries: [
            ...latest.entries,
            {
              id: nextId.current++,
              role: 'assistant',
              text: reply.text,
              cars: reply.cars,
              action: reply.suggestedAction,
            },
          ],
        }));
      } catch (error) {
        if (generation.current[surface] !== asked) return;

        // The message was not answered: back into the field, so it is not typed twice.
        update(surface, (latest) => ({
          ...latest,
          pending: false,
          failure: describeAssistantFailure((error ?? {}) as RequestFailure),
          text: latest.text || message,
          entries: latest.entries.filter((entry) => entry.id !== personEntry.id),
        }));
      }
    },
    [update]
  );

  // The conversations are shown only to the owner they belong to. While the session is checked
  // again (signed out and in as someone else in another tab, the api not answering yet) and in the
  // render where the hands change before the wipe, the window is a clean sheet — never the person
  // before's lines, draft or conversation id. A visitor's first conversation, started before the
  // first answer of the api, belongs to nobody yet and stays.
  const visible = conversations.owner === owner;
  const shown = useMemo(
    () => (visible ? conversations : { ...NONE, owner }),
    [visible, conversations, owner]
  );
  const value = useMemo(
    () => ({ conversations: shown, setText, submit, startOver }),
    [shown, setText, submit, startOver]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

/** The conversation of one catalog, and what the window does with it. */
export const useAssistantConversation = (surface: AssistantSurface): AssistantConversation => {
  const context = useContext(Context);

  if (!context)
    throw new Error('useAssistantConversation is read outside AssistantConversationsProvider');

  const conversation = context.conversations[surface];

  return {
    ...conversation,
    over: !!conversation.failure?.over || conversation.remaining === 0,
    setText: (text) => context.setText(surface, text),
    submit: () => context.submit(surface),
    startOver: () => context.startOver(surface),
  };
};
